import React, { useEffect, useState } from 'react';
import { useApp } from '@/contexts/AppContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Pin, Plus } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

/**
 * Pin packet picker — allows the user to either:
 *  - select an existing audit bundle and pin the current packet to it, OR
 *  - create a new audit bundle with this packet as the first pin.
 */
export default function PinToBundleDialog({ open, onOpenChange, investigationId, onPinned }) {
  const { API, seed } = useApp();
  const [mode, setMode] = useState('existing');
  const [bundles, setBundles] = useState([]);
  const [bundleId, setBundleId] = useState('');
  const [name, setName] = useState('');
  const [framework, setFramework] = useState('SOC 2');
  const [busy, setBusy] = useState(false);
  const [loadingBundles, setLoadingBundles] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoadingBundles(true);
    axios.get(`${API}/audit-bundles`).then((r) => {
      setBundles(r.data.items);
      setMode(r.data.items.length > 0 ? 'existing' : 'new');
      if (r.data.items[0]) setBundleId(r.data.items[0].id);
      setLoadingBundles(false);
    }).catch(() => setLoadingBundles(false));
  }, [open, API]);

  const submit = async () => {
    setBusy(true);
    try {
      let bundle;
      if (mode === 'existing') {
        if (!bundleId) { toast.error('Pick a bundle'); return; }
        const r = await axios.post(`${API}/audit-bundles/${bundleId}/pins`, { investigation_id: investigationId });
        bundle = r.data;
      } else {
        if (!name.trim()) { toast.error('Bundle name required'); return; }
        const r = await axios.post(`${API}/audit-bundles`, {
          name: name.trim(),
          framework,
          initial_packets: [investigationId],
        });
        bundle = r.data;
      }
      toast.success('Pinned to bundle', { description: bundle.name });
      onPinned?.(bundle);
      onOpenChange(false);
    } catch (e) {
      toast.error('Could not pin to bundle');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pin-bundle-dialog" className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold tracking-tight text-apex-ink flex items-center gap-2">
            <Pin className="w-4 h-4 text-apex-lens" />
            Pin to Audit Bundle
          </DialogTitle>
          <DialogDescription className="text-sm text-apex-neutral">
            Build a custom bundle of evidence packets tied to a specific control or audit cycle.
            Currently pinning <span className="font-mono text-apex-ink">{investigationId}</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Mode toggle */}
          <div className="inline-flex items-center bg-slate-100 rounded-md p-0.5 gap-0.5">
            <button
              type="button"
              onClick={() => setMode('existing')}
              data-testid="pin-mode-existing"
              disabled={bundles.length === 0}
              className={`text-xs font-semibold px-3 py-1.5 rounded transition-colors ${mode === 'existing' ? 'bg-white text-apex-ink shadow-sm' : 'text-apex-neutral'} disabled:opacity-50`}
            >
              Existing bundle
            </button>
            <button
              type="button"
              onClick={() => setMode('new')}
              data-testid="pin-mode-new"
              className={`text-xs font-semibold px-3 py-1.5 rounded transition-colors ${mode === 'new' ? 'bg-white text-apex-ink shadow-sm' : 'text-apex-neutral'}`}
            >
              New bundle
            </button>
          </div>

          {mode === 'existing' && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-apex-neutral">Choose bundle</Label>
              {loadingBundles ? (
                <div className="text-xs text-apex-neutral inline-flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" />Loading…</div>
              ) : bundles.length === 0 ? (
                <p className="text-xs text-apex-neutral">No bundles yet — create one below.</p>
              ) : (
                <Select value={bundleId} onValueChange={setBundleId}>
                  <SelectTrigger data-testid="pin-bundle-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {bundles.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.name} · {b.framework} ({b.pins.length} pins)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {mode === 'new' && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="bundle-name" className="text-xs font-semibold uppercase tracking-wider text-apex-neutral">Bundle name</Label>
                <Input id="bundle-name" data-testid="pin-bundle-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. SOC 2 CC6.1 Q2 cycle" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider text-apex-neutral">Framework</Label>
                <Select value={framework} onValueChange={setFramework}>
                  <SelectTrigger data-testid="pin-bundle-framework"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(seed?.audit_workspace?.frameworks ?? [{ name: 'SOC 2' }, { name: 'ISO 27001' }, { name: 'PCI DSS' }, { name: 'DORA' }]).map((f) => (
                      <SelectItem key={f.name} value={f.name}>{f.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="pin-submit" className="bg-apex-lens hover:bg-apex-lens-dark text-white gap-1.5">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" />Pinning…</> : <><Plus className="w-4 h-4" />Pin packet</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
